/**
 * OEMConfig（サブドメイン → 設定）と画面反映の対応
 *
 * 【有効 — コードで参照されている】
 * - http_host              → utils/axios.js baseURL（API 基底）
 * - root_host              → pages/LoginByRoot.js
 *                            ※ 自動導出: {subdomain}crm.{parentDomain}（OEM は空文字で無効化）
 * - favicon_path           → App.js（truthy のとき favicon 更新）
 * - login_logo_image_path  → features/user/LandingIntro.js（ログイン画面の左カラムの画像）
 * - background_color_login_card → LandingIntro（左カラム部分の背景色。全面塗りつぶし。）
 * - font_family            → LandingIntro, Login.js, ResetPassword.js
 * - background_color_login → Login.js, ResetPassword.js（ログイン画面自体の背景色。全面塗りつぶし。）（ForgotPassword は未反映）
 * - header_full_color / header_text_color → Header.js, Login/ResetPassword/ForgotPassword の言語ボタン。文字色。右上のアカウントマークのドロップダウン文字色にも反映されるため、白はNG。
 * - side_menu_color        → containers/LeftSidebar.js。ログイン後のサイドメニュー背景色。
 * - dashboard_title        → LeftSidebar（i18n キーとして t() に渡る。生文字列はキー名にする）ログイン後の左上の文字列テキスト。
 * - user_name / user_role  → LeftSidebar 下部表示。ログイン後の左下の文字列テキスト。空白の場合はなにも表示されない。
 * - background_color_in_full_content / content_padding / border_radius_global / box_shadow_level
 *                          → containers/PageContent.js の main
 *
 * 【未使用 — 設定しても現状 UI に出ない】
 * - login_logo_header_path
 * - dashboard_logo_image_path（サイドバーはテキストタイトルのみ）
 * - api_host（実際は http_host のみ使用）
 * - user_initials
 * - login_brand_name
 *
 * 【読み込みのみ / 実質効かない】
 * - login_text → Login/ResetPassword で変数化されるが表示はコメントアウト
 * - text_color_login_header → LandingIntro で取得するが style に未適用
 *
 * default 分岐では config が空になり得る（http_host 未定義など）。未知サブドメインに注意。
 *
 * 【ドメイン対応】
 * サブドメインのみで判定するため同一サブドメインは新旧どちらの親ドメイン
 * (optimize-business.com / optimize-business-seo.com) でも同じ設定が適用される。
 * root_host は hostname から自動導出されるため親ドメインの違いも吸収する。
 */
const getSubDomain = () => {
  const parts = window.location.hostname.split('.');
  const subdomain = parts.shift();
  return subdomain;
};

/**
 * Derive root_host from the current hostname.
 * root subdomain = {clientSubdomain}crm under the same parent domain.
 * e.g. dev.optimize-business-seo.com  → devcrm.optimize-business-seo.com
 *      seo.optimize-business-seo.com  → seocrm.optimize-business-seo.com
 */
const getRootHost = () => {
  const { protocol, hostname } = window.location;

  if (hostname === 'localhost') {
    return `${protocol}//localhost:3001`;
  }

  const parts = hostname.split('.');
  const subdomain = parts[0];
  const parentDomain = parts.slice(1).join('.');
  return `${protocol}//${subdomain}crm.${parentDomain}`;
};

const getOEMConfig = () => {
  const config = {};

  switch (getSubDomain()) {
    case 'seo-client':
      config.http_host = process.env.REACT_APP_BASE_URL;
      config.login_logo_header_path = '/intro.png';
      config.login_logo_image_path = '/intro.png';
      config.dashboard_logo_image_path = '/intro.png';
      config.api_host = `${process.env.REACT_APP_BASE_URL}/v1`;
      config.favicon_path = '/favicon.ico';
      config.user_name = 'SEO Prototype';
      config.user_role = 'SEO Admin';
      config.user_initials = '/avatar.png';
      config.side_menu_color = '#ffffff';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'localhost': // ローカル
      config.http_host = process.env.REACT_APP_BASE_URL;
      config.login_logo_header_path = '/seo_logo.png';
      config.login_logo_image_path = '/seo_logo.png';
      config.dashboard_logo_image_path = '/seo_logo.png';
      config.api_host = `${process.env.REACT_APP_BASE_URL}/v1`;
      config.favicon_path = '/favicon.ico';
      config.user_name = 'SEO Prototype';
      config.user_role = 'SEO Admin';
      config.user_initials = '/avatar.png';
      config.side_menu_color = '#ffffff';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'seo-dev': // 開発（旧ドメイン: seo-dev.optimize-business.com）
    case 'dev': // 開発（新ドメイン: dev.optimize-business-seo.com）
      config.http_host = 'https://seo-dev-api.optimize-business.com/api';
      // config.login_logo_header_path = null;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/dev_dashboard_logo.svg`;
      // config.dashboard_logo_image_path = null;
      config.api_host = '';
      config.favicon_path = null;
      config.user_name = 'SEO Prototype DEV';
      config.user_role = 'SEO Admin DEV';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '#fafafa';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      // config.login_brand_name = '';
      // config.login_text = '';
      config.background_color_login_card = '#eaf2ff';
      // config.text_color_login_header = '';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'seo-stg': // ステージング（旧ドメイン: seo-stg.optimize-business.com）
    case 'stg': // ステージング（新ドメイン: stg.optimize-business-seo.com）
      config.http_host = 'https://seo-stg-api.optimize-business.com/api';
      config.login_logo_header_path = null;
      config.login_logo_image_path = null;
      config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.api_host = '';
      config.favicon_path = null;
      config.user_name = 'SEO Prototype';
      config.user_role = 'SEO Admin';
      config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'seo': // 本番（旧: seo.optimize-business.com / 新: seo.optimize-business-seo.com）
      config.http_host = 'https://seo-api.optimize-business.com/api';
      config.login_logo_header_path = null;
      config.login_logo_image_path = null;
      config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.api_host = '';
      config.favicon_path = null;
      config.user_name = 'SEO Prototype';
      config.user_role = 'SEO Admin';
      config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      // config.dashboard_title = 'MOB SEOツール';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'mob': // 株式会社MOB
      config.http_host = 'https://mob-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/mob_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/mob_dashboard_logo.svg`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/mob_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = 'MOB';
      config.user_role = '記事作成運用者';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/mob_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      // config.dashboard_title = 'MOB SEOツール';
      config.dashboard_title = 'LOGO_TITLE';
      break;
    case 'user': // 株式会社アシスト
      config.http_host = 'https://user-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/user_dashboard_logo.svg`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = 'Contents Monster';
      break;
    case 'assist': // 株式会社アシスト（2）
      config.http_host = 'https://assist-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = null;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'sk': // 株式会社エス・ケイ通信
      config.http_host = 'https://sk-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/sk_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'rg': // 株式会社ライジング
      config.http_host = 'https://rg-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/rg_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'fr': // 株式会社フロンティア
      config.http_host = 'https://fr-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/fr_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'cg': // 株式会社チェンジ
      config.http_host = 'https://cg-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/cg_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'rl': // 株式会社リアライズ
      config.http_host = 'https://rl-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/rl_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'ce': // 株式会社センタリング
      config.http_host = 'https://ce-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/ce_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'tr': // 株式会社トラスト
      config.http_host = 'https://tr-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/tr_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    case 'jp': // 株式会社ジャパンネット
      config.http_host = 'https://jp-seo-api.optimize-business.com/api';
      // config.login_logo_header_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.login_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/jp_dashboard_logo.png`;
      // config.dashboard_logo_image_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.api_host = '';
      config.root_host = '';
      config.favicon_path = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/SEO.png`;
      config.user_name = '';
      config.user_role = '';
      // config.user_initials = `${process.env.REACT_APP_AWS_S3_BASE_URL}/files/uploaded/production/oems/xxx_dashboard_logo.svg`;
      config.side_menu_color = '';
      config.header_full_color = '';
      config.header_text_color = '';
      config.background_color_in_full_content = '';
      config.background_color_login = '';
      config.font_family = '';
      config.content_padding = '';
      config.border_radius_global = '';
      config.box_shadow_level = '';
      config.login_brand_name = '';
      config.login_text = '';
      config.background_color_login_card = '';
      config.text_color_login_header = '';
      config.dashboard_title = '記事作成・被リンク\n獲得ツール';
      break;
    default:
      break;
  }

  // Derive root_host from hostname for seo sites
  if (config.root_host === undefined) {
    config.root_host = process.env.REACT_APP_ROOT_URL ?? getRootHost();
  }

  return config;
};

export default getOEMConfig;
